import { Button, ButtonLink } from "@lumi/ui-headless";
import { Link, useNavigate, useParams } from "react-router-dom";
import { StatCard } from "../../shared/components/StatCard";
import { SubmissionCard } from "../../shared/components/SubmissionCard";
import { useProviderDetails } from "./hooks/useProviders";
import { LoadingSpinner } from "../../shared/components/loading/LoadingSpinner";

/**
 * Recent Submissions Section Component
 */
interface RecentSubmission {
  id: string;
  title: string;
  status: string;
  synopsis: string | null;
  posterUrl: string | null;
  year: number | null;
  durationMinutes: number | null;
  director: string | null;
  submittedAt?: string | null;
  createdAt: string;
}

interface RecentSubmissionsSectionProps {
  readonly recentSubmissions: RecentSubmission[];
}

function RecentSubmissionsSection({ recentSubmissions }: RecentSubmissionsSectionProps) {
  if (recentSubmissions.length === 0) {
    return <p className="text-neutral-400 text-center py-8">No submissions yet</p>;
  }

  return (
    <div className="space-y-4">
      {recentSubmissions.map((submission) => {
        const displayDate = submission.submittedAt || submission.createdAt;
        return (
          <SubmissionCard
            key={submission.id}
            submission={submission}
            subtitle={
              <span className="text-sm text-neutral-500">
                {new Date(displayDate).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </span>
            }
            actions={
              <ButtonLink to={`/admin/review/${submission.id}`} variant="outline">
                Review
              </ButtonLink>
            }
          />
        );
      })}
    </div>
  );
}

/**
 * Provider Detail Page - Detailed view of a content provider
 * Shows provider information and complete submission history
 */
export function ProviderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { provider, loading, error } = useProviderDetails(id);

  if (loading) {
    return (
      <div className="mx-auto py-8 px-4">
        <div className="text-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  if (error || !provider) {
    return (
      <div className="mx-auto py-8 px-4">
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 text-red-400">
          <p className="font-semibold">Error loading provider</p>
          <p className="text-sm mt-1">{error || "Provider not found"}</p>
        </div>
        <div className="text-center mt-4">
          <Link to="/admin/providers" className="text-lumi-accent hover:underline">
            ← Back to Providers
          </Link>
        </div>
      </div>
    );
  }

  const { stats } = provider;

  return (
    <div className="mx-auto space-y-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">{provider.provider.email}</h1>
            {provider.provider.organization && (
              <p className="text-sm text-neutral-400 pb-6">{provider.provider.organization}</p>
            )}
            <p className="text-sm text-neutral-500 mt-1">
              Joined{" "}
              {new Date(provider.provider.createdAt).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          </div>
          <Button variant="ghost-sm" onClick={() => navigate("/admin/providers")}>
            ← Back to Providers
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <StatCard label="Total" value={stats.total} color="text-white" variant="centered" />
        <StatCard label="Draft" value={stats.draft} color="text-neutral-400" variant="centered" />
        <StatCard label="Pending" value={stats.pending} color="text-yellow-500" variant="centered" />
        <StatCard label="Approved" value={stats.approved} color="text-green-500" variant="centered" />
        <StatCard label="Rejected" value={stats.rejected} color="text-red-500" variant="centered" />
        <StatCard label="Published" value={stats.published} color="text-blue-500" variant="centered" />
      </div>

      {/* Recent Submissions */}
      <div className="rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white mb-4">Recent Submissions</h2>
          <Button variant="ghost-sm" onClick={() => navigate(`/admin/review-queue?provider=${id}`)}>
            View All Submissions
          </Button>
        </div>
        <RecentSubmissionsSection recentSubmissions={provider.recentSubmissions} />
      </div>
    </div>
  );
}
